import React from 'react';
import { useSaveTextDataQuery } from 'modules/shared/queries';
import { ResetButton, useDefaultSettingsContext } from '../components';
import { getCurrentDate } from 'common';
import { useConfigScopeSelectors } from 'modules/core/scopes';
import { useQueryClient } from 'react-query';
import { SharedReactQueryKey } from 'modules/shared/queries/query-enum';

const getCommonPayloadData = () => ({
    'BinaryData': [],
    'ContentType': 'json',
    'CreatedDate': getCurrentDate(),
    'ELEMENTID': ''
});

export const useDefaultSettingsPayload = () => {
    const currentUserDetails = useConfigScopeSelectors().selectCurrentUserDetails();
    const companyDetails = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `CompanyDetails_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': '{"CompanyName":{"Content":""},"CompanyWebsite":{"Content":""},"CompanySupportEmail":{"Content":""},"CompanyInfoEmail":{"Content":""},"CopyrightInfo":{"Content":""}}'
    });
    const authenticationSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `AuthenticationSettings_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': JSON.stringify({
            'ServerConfiguration': {
                'DevelopmentServerInstanceUrl': '',
                'StagingServerInstanceUrl': '',
                'ProductionServerInstanceUrl': ''
            },
            'SalesforceConfiguration': {
                'ConsumerKey': '',
                'ConsumerSecret': '',
                'isSelected': false
            },
            'OfficeConfiguration': {
                'ClientId': '',
                'ReturnURL': '',
                'Resource': '',
                'isSelected': false
            },
            'OfficeConfigurationChina': {
                'ClientId': '',
                'ReturnURL': '',
                'Resource': '',
                'isSelected': false
            },
            'IdentityServerConfiguration': {
                'ClientId': '',
                'Authority': '',
                'isSelected': false
            },
            'OktaConfiguration': {
                'ClientId': '',
                'Authority': '',
                'isSelected': false
            },
            'GoogleConfiguration': {
                'ClientId': '',
                'ReturnURL': '',
                'Resource': '',
                'isSelected': false
            },
            'ActiveDirectoryConfiguration': {
                'Domain': '',
                'isSelected': false
            },
            'AgilePiontNXConfiguration': {
                'isSelected': true
            },
            'PingFederateConfiguration': {
                'ClientId': '',
                'Authority': '',
                'UsernameClaimType': '',
                'isSelected': false
            },
            'AmazonCognitoConfiguration': {
                'ClientId': '',
                'Authority': '',
                'UserImpersonationScopeURL': '',
                'isSelected': false
            },
            'defaultConfig': 'AgilePiontNXConfiguration'
        })
    });
    const applicationListSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `ApplicationList_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': '[]'
    });
    const notificationListSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `NotificationList_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': JSON.stringify('')
    });
    const appMenusSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `AppMenus_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': JSON.stringify({
            locales: 'default',
            menuOptions: {
                default: {
                    'MyApps': { 'fieldValue': 'My Apps', 'isEnabled': true },
                    'MyTasks': { 'fieldValue': 'My Tasks', 'isEnabled': true },
                    'Outbox': { 'fieldValue': 'Outbox', 'isEnabled': true },
                    'TeamTasks': { 'fieldValue': 'Team Tasks', 'isEnabled': true },
                    'PlannedTasks': { 'fieldValue': 'Planned Tasks', 'isEnabled': true },
                    'Watchlist': { 'fieldValue': 'Watchlist', 'isEnabled': true },
                    'ExternalApps': { 'fieldValue': 'ExternalApps', 'isEnabled': true },
                    'ProcessesIStarted': { 'fieldValue': 'Processes I Started', 'isEnabled': true },
                    'ProcessesIWorked': { 'fieldValue': 'Processes I\'ve Worked', 'isEnabled': true },
                    'Delegations': { 'fieldValue': 'Delegations', 'isEnabled': true },
                    'Settings': { 'fieldValue': 'Settings', 'isEnabled': true },
                    'Help': { 'fieldValue': 'Help', 'isEnabled': true },
                    'About': { 'fieldValue': 'About', 'isEnabled': true }
                }
            }
        })
    });
    const appThemeSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `AppTheme_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': JSON.stringify({
            'Blue': { 'isSelected': true },
            'Red': { 'isSelected': false },
            'Green': { 'isSelected': false },
            'Orange': { 'isSelected': false },
            'Custom': { 'isSelected': false },
            'previewImage': 'Blue',
            'backgroundColorCode': '#537db3',
            'menuColorCode': '#1f7cf5',
            'buttonColorCode': '#15437E'
        })
    });
    const globalAppImagesSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `GlobalAppImages_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': '"[]"'
    });
    const pushNotificationsSettings = () => ({
        ...getCommonPayloadData(),
        'SecondaryReference': `PushNotifications_${currentUserDetails.userName.replace(/\\/g, '_')}`,
        'TextData': JSON.stringify({
            hostUrl: '',
            domain: '',
            value1: '',
            value2: ''
        })
    });
    const getPayloadDetailsByType = (type?: string) => {
        switch (type) {
            case 'company-details':
                return { companyDetails: companyDetails() };
            case 'authentication':
                return { authenticationSettings: authenticationSettings() };
            case 'app-list':
                return { applicationListSettings: applicationListSettings() };
            case 'menu-options':
                return { appMenusSettings: appMenusSettings() };
            case 'styling':
                return { appThemeSettings: appThemeSettings() };
            case 'upload-images':
                return { globalAppImagesSettings: globalAppImagesSettings() };
            case 'notification-list':
                return { notificationListSettings: notificationListSettings() };
            case 'push-notifications':
                return { pushNotificationsSettings: pushNotificationsSettings() };
            default:
                return {
                    companyDetails: companyDetails(),
                    authenticationSettings: authenticationSettings(),
                    applicationListSettings: applicationListSettings(),
                    appMenusSettings: appMenusSettings(),
                    appThemeSettings: appThemeSettings(),
                    globalAppImagesSettings: globalAppImagesSettings(),
                    notificationListSettings: notificationListSettings(),
                    pushNotificationsSettings: pushNotificationsSettings()
                };
        }
    };
    return { getPayloadDetailsByType };
};

export const ResetDefaultSettingsContainer = React.memo(() => {
    const { activeTab } = useDefaultSettingsContext();
    const { mutateAsync } = useSaveTextDataQuery();
    const { mutateAsync: saveAuthenticationSettings } = useSaveTextDataQuery();
    const { mutateAsync: saveApplicationListSettings } = useSaveTextDataQuery();
    const { mutateAsync: saveNotificationListSettings } = useSaveTextDataQuery();
    const { mutateAsync: saveAppMenusSettings } = useSaveTextDataQuery();
    const { mutateAsync: saveAppThemeSettings } = useSaveTextDataQuery();
    const { mutateAsync: saveGlobalAppImagesSettings } = useSaveTextDataQuery();
    const { mutateAsync: savePushNotificationSettings } = useSaveTextDataQuery();
    const queryClient = useQueryClient();
    const { setDefaultSettingsConfig } = useDefaultSettingsContext();
    const { getPayloadDetailsByType } = useDefaultSettingsPayload();

    const invokeQueryInvalidation = React.useCallback(() => {
        switch (activeTab) {
            case 'company-details':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_COMPANY_DETAILS);
                break;
            case 'authentication':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_AUTHENTICATION_SETTINGS);
                break;
            case 'app-list':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_APPLICATION_LIST);
                break;
            case 'menu-options':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_MENU_OPTIONS);
                break;
            case 'styling':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_APP_THEME);
                break;
            case 'upload-images':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_GLOBAL_APP_IMAGE);
                break;
            case 'notification-list':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_NOTIFICATION_LIST);
                break;
            case 'push-notifications':
                queryClient.invalidateQueries(SharedReactQueryKey.GET_PUSH_NOTIFICAITONS);
                break;
            default:
        }
    }, [activeTab, queryClient]);

    const onReset = React.useCallback(async () => {
        const { companyDetails, authenticationSettings, applicationListSettings, notificationListSettings,
            appMenusSettings, appThemeSettings, globalAppImagesSettings, pushNotificationsSettings } = getPayloadDetailsByType();

        await Promise.all([
            mutateAsync(companyDetails!),
            saveAuthenticationSettings(authenticationSettings!),
            saveApplicationListSettings(applicationListSettings!),
            saveNotificationListSettings(notificationListSettings!),
            saveAppMenusSettings(appMenusSettings!),
            saveAppThemeSettings(appThemeSettings!),
            saveGlobalAppImagesSettings(globalAppImagesSettings!),
            savePushNotificationSettings(pushNotificationsSettings!)]);

        invokeQueryInvalidation();
        setDefaultSettingsConfig({ doesNotIncludesAppListConfig: true, doesNotIncludesNotificationList: true });

    }, [getPayloadDetailsByType, mutateAsync, saveAuthenticationSettings, saveApplicationListSettings,
        saveNotificationListSettings, saveAppMenusSettings, saveAppThemeSettings, saveGlobalAppImagesSettings,
        savePushNotificationSettings, invokeQueryInvalidation, setDefaultSettingsConfig]);

    return (
        <ResetButton onReset={onReset} />
    );
});
